import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AppStatusBadge, PaymentBadge } from "@/components/Badges";
import { appApi, paymentApi } from "@/lib/api";
import { toast } from "sonner";
import StepProfile from "@/pages/steps/StepProfile";
import StepQuestionnaire from "@/pages/steps/StepQuestionnaire";
import StepDocuments from "@/pages/steps/StepDocuments";
import { Loader2, Check, Send, ShieldCheck, FileCheck2, ClipboardList, Clock, Info, XCircle, Award, CreditCard, Landmark, Ticket } from "lucide-react";

const STEPS = ["Company profile", "Questionnaire", "Documents", "Submit & pay"];

const Stepper = ({ current, setStep }) => (
  <div data-testid="application-stepper" className="flex flex-wrap gap-2">
    {STEPS.map((s, i) => {
      const active = i === current;
      const done = i < current;
      return (
        <button key={s} onClick={() => setStep(i)} className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${active ? "bg-navy-900 text-white" : done ? "bg-wgreen-100 text-wgreen-700" : "bg-[var(--wechr-subtle)] text-navy-700"}`}>
          <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${active ? "bg-white text-navy-900" : done ? "bg-wgreen-600 text-white" : "bg-white text-navy-700"}`}>{done ? <Check className="h-3 w-3" /> : i + 1}</span>
          <span className="hidden sm:inline">{s}</span>
        </button>
      );
    })}
  </div>
);

// Applicant-facing status timeline. The AI gap review is an internal admin tool
// and is intentionally NOT shown to applicants.
const STATUS_STEPS = [
  { key: "submitted", label: "Application submitted", desc: "Your application and documents were received.", Icon: FileCheck2 },
  { key: "payment", label: "Payment", desc: "Pay the certification fee to enter the review queue.", Icon: CreditCard },
  { key: "in_review", label: "Under review", desc: "A WECH-RB reviewer is verifying your documents.", Icon: ClipboardList },
  { key: "decision", label: "Decision", desc: "Approval, rejection, or a request for more information.", Icon: Info },
  { key: "certified", label: "Certificate issued", desc: "Your certificate is available after admin approval.", Icon: Award },
];

// Number of completed timeline steps for a given status.
const STATUS_ORDER = { draft: 0, submitted: 1, paid: 2, in_review: 3, needs_info: 3, enhanced_review: 3, approved: 4, rejected: 4, certified: 5 };

const StatusTimeline = ({ status, payment }) => {
  let pos = STATUS_ORDER[status] ?? 0;
  const paid = payment?.status === "paid";
  // If somehow status is still 'submitted' but payment cleared, reflect payment done.
  if (status === "submitted" && paid) pos = 2;
  return (
    <ol className="relative space-y-6 border-l border-[var(--wechr-border)] pl-6" data-testid="applicant-status-timeline">
      {STATUS_STEPS.map((s, i) => {
        const reached = pos >= i + 1;
        const isDecision = s.key === "decision";
        const isPayment = s.key === "payment";
        const rejected = status === "rejected";
        const needsInfo = ["needs_info", "enhanced_review"].includes(status);
        let Icon = s.Icon;
        let tone = reached ? "bg-wgreen-600 text-white" : "bg-[var(--wechr-subtle)] text-navy-700";
        let label = s.label;
        if (isPayment) {
          if (paid) { Icon = Check; label = "Payment received"; }
          else if (payment?.status === "processing") { Icon = Clock; tone = "bg-gold-500 text-white"; label = "Payment processing"; }
        }
        if (isDecision && reached) {
          if (rejected) { Icon = XCircle; tone = "bg-[#B42318] text-white"; label = "Application rejected"; }
          else if (needsInfo) { Icon = Info; tone = "bg-[#9A3412] text-white"; label = "Additional information requested"; }
          else { Icon = Check; label = "Approved"; }
        }
        return (
          <li key={s.key} className="relative">
            <span className={`absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full ${tone}`}><Icon className="h-3.5 w-3.5" /></span>
            <p className={`font-medium ${reached ? "text-navy-900" : "text-[var(--wechr-muted-text)]"}`}>{label}</p>
            <p className="text-sm text-[var(--wechr-muted-text)]">{s.desc}</p>
          </li>
        );
      })}
    </ol>
  );
};

const formatMoney = (n) => {
  const v = Number(n || 0);
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const PaymentCard = ({ application }) => {
  const [paying, setPaying] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // { code, percent_off, discounted_amount }
  const [checkingPromo, setCheckingPromo] = useState(false);
  const payment = application.payment || {};
  const base = Number(application.price_usd || 0);
  const finalPrice = promo ? Number(promo.discounted_amount) : base;

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setCheckingPromo(true);
    try {
      const { data } = await appApi.validatePromo(application.id, code);
      if (data.valid) {
        setPromo({ code: data.code, percent_off: data.percent_off, discounted_amount: data.discounted_amount });
        toast.success(data.message || "Promotion code applied");
      } else {
        setPromo(null);
        toast.error(data.message || "Invalid promotion code");
      }
    } catch (err) {
      setPromo(null);
      toast.error(err.response?.data?.detail || "Could not validate code");
    } finally { setCheckingPromo(false); }
  };

  const removePromo = () => { setPromo(null); setPromoInput(""); };

  const startPayment = async () => {
    setPaying(true);
    try {
      const { data } = await appApi.checkout(application.id, window.location.origin, promo?.code);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start payment");
        setPaying(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  if (payment.status === "processing") {
    return (
      <Card className="mb-6 border-gold-500" data-testid="payment-processing-card">
        <CardContent className="flex flex-col gap-3 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gold-100"><Clock className="h-5 w-5 text-gold-700" /></div>
            <div>
              <p className="font-heading text-sm font-semibold text-navy-900">Bank payment processing</p>
              <p className="text-sm text-[var(--wechr-muted-text)]">Your ACH bank payment is settling (a few business days). Review begins automatically once it clears.</p>
            </div>
          </div>
          <PaymentBadge status="processing" testId="payment-status-badge" />
        </CardContent>
      </Card>
    );
  }

  const isFree = promo && Number(finalPrice) <= 0;

  return (
    <Card className="mb-6 border-wgreen-600" data-testid="payment-due-card">
      <CardContent className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-wgreen-100"><CreditCard className="h-5 w-5 text-wgreen-700" /></div>
            <div>
              <p className="font-heading text-sm font-semibold text-navy-900">Complete payment to begin review</p>
              <p className="text-sm text-[var(--wechr-muted-text)]">{_scopeSummary(application)}</p>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--wechr-muted-text)]">
                <span className="inline-flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" /> Credit / debit card</span>
                <span className="inline-flex items-center gap-1"><Landmark className="h-3.5 w-3.5" /> Bank transfer (ACH)</span>
              </p>
            </div>
          </div>
          <div className="text-right">
            {promo ? (
              <>
                <p className="text-sm text-[var(--wechr-muted-text)] line-through" data-testid="payment-original-amount">{formatMoney(base)} USD</p>
                <p className="font-heading text-2xl font-semibold text-navy-900" data-testid="payment-amount">{formatMoney(finalPrice)}<span className="ml-1 text-sm font-normal text-[var(--wechr-muted-text)]">USD</span></p>
                <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-wgreen-100 px-2 py-0.5 text-xs font-semibold text-wgreen-700" data-testid="payment-discount-badge">{promo.percent_off}% off · {promo.code}</span>
              </>
            ) : (
              <p className="font-heading text-2xl font-semibold text-navy-900" data-testid="payment-amount">{formatMoney(base)}<span className="ml-1 text-sm font-normal text-[var(--wechr-muted-text)]">USD</span></p>
            )}
          </div>
        </div>

        {/* Promotion code */}
        <div className="mt-4 rounded-xl border border-[var(--wechr-border)] bg-[var(--wechr-subtle)] p-3">
          {promo ? (
            <div className="flex items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-sm text-navy-800"><Check className="h-4 w-4 text-wgreen-700" /> Code <span className="font-mono font-semibold">{promo.code}</span> applied ({promo.percent_off}% off)</p>
              <Button variant="ghost" size="sm" onClick={removePromo} data-testid="promo-remove-button">Remove</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="flex items-center gap-1 text-xs font-medium text-navy-800 sm:w-44"><Ticket className="h-3.5 w-3.5" /> Have a promotion code?</label>
              <div className="flex flex-1 gap-2">
                <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="e.g. WECH-ABC123" className="uppercase" data-testid="promo-code-input" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }} />
                <Button variant="outline" onClick={applyPromo} disabled={checkingPromo || !promoInput.trim()} data-testid="promo-apply-button">
                  {checkingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                </Button>
              </div>
            </div>
          )}
        </div>

        <Button className="btn-green mt-4 w-full" onClick={startPayment} disabled={paying} data-testid="pay-now-button">
          {paying ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {isFree ? "Confirming…" : "Redirecting to secure checkout…"}</> : (isFree ? <><Check className="mr-2 h-4 w-4" /> Confirm free certification &amp; start review</> : <><CreditCard className="mr-2 h-4 w-4" /> Pay {formatMoney(finalPrice)} &amp; start review</>)}
        </Button>
        <p className="mt-2 text-center text-xs text-[var(--wechr-muted-text)]">{isFree ? "Your promotion code covers the full fee — no payment required." : "Payments are processed securely by Stripe. WECH-RB never stores your card or bank details."}</p>
      </CardContent>
    </Card>
  );
};

const _scopeSummary = (a) => {
  const yrs = `${a.duration_years || 1}-year validity`;
  if (a.scope_type === "category") {
    const n = (a.selected_categories || []).length;
    return `Category-based certification · ${n} categor${n === 1 ? "y" : "ies"} · ${yrs}`;
  }
  return `Full certification · all categories · ${yrs}`;
};

const SubmitStep = ({ application, reload, goBack }) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const submitted = ["submitted", "paid", "in_review", "needs_info", "enhanced_review", "approved", "rejected", "certified"].includes(application.status);
  const canResubmit = ["needs_info", "enhanced_review"].includes(application.status);
  const paid = application.payment?.status === "paid";
  const needsPayment = submitted && !paid && !application.certificate_id && !["approved", "rejected", "certified"].includes(application.status);

  const submit = async () => {
    setSubmitting(true);
    try {
      await appApi.submit(application.id);
      toast.success("Submitted — complete payment to begin review");
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      <h2 className="font-heading text-lg font-semibold text-navy-900">Submit &amp; pay</h2>
      <p className="mb-6 text-sm text-[var(--wechr-muted-text)]">Submit your application, then complete payment. A WECH-RB reviewer verifies your documents once payment is received. You'll be notified by email as your status changes.</p>

      {application.certificate_id ? (
        <Card><CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-wgreen-100"><ShieldCheck className="h-7 w-7 text-wgreen-700" /></div>
          <div>
            <p className="font-heading font-semibold text-navy-900">Your certificate has been issued</p>
            <p className="mt-1 text-sm text-[var(--wechr-muted-text)]">Congratulations — your WECH-RB certification is now active.</p>
          </div>
          <div className="flex items-center gap-2"><span className="text-sm text-[var(--wechr-muted-text)]">Status:</span> <AppStatusBadge status={application.status} /></div>
          <Link to={`/certificate/${application.certificate_id}`}><Button className="btn-green" data-testid="view-certificate-button"><ShieldCheck className="mr-2 h-4 w-4" /> View your certificate</Button></Link>
        </CardContent></Card>
      ) : submitted ? (
        <>
          {needsPayment && <PaymentCard application={application} />}
          <Card><CardContent className="grid gap-6 py-8 sm:grid-cols-[auto,1fr] sm:gap-10 sm:px-8">
            <div className="flex flex-col items-center gap-2 sm:items-start">
              <span className="text-sm text-[var(--wechr-muted-text)]">Current status</span>
              <AppStatusBadge status={application.status} testId="applicant-status-badge" />
              <PaymentBadge status={application.payment?.status} testId="applicant-payment-badge" />
              {["needs_info", "enhanced_review"].includes(application.status) && (
                <p className="mt-2 max-w-xs text-sm text-[#9A3412]">The reviewer requested additional information. Update your documents, then re-submit.</p>
              )}
            </div>
            <div>
              <StatusTimeline status={application.status} payment={application.payment} />
              <div className="mt-6 flex flex-wrap gap-2">
                {canResubmit && (
                  <Button className="btn-green" onClick={submit} disabled={submitting} data-testid="resubmit-application-button">
                    {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Re-submit for review
                  </Button>
                )}
                <Button variant="outline" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
              </div>
            </div>
          </CardContent></Card>
        </>
      ) : (
        <Card><CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          <div className="flex items-center gap-2"><span className="text-sm text-[var(--wechr-muted-text)]">Current status:</span> <AppStatusBadge status={application.status} /></div>
          <p className="max-w-md text-sm text-navy-800">Make sure you've completed the questionnaire and uploaded your supporting documents for each topic. All document topics are required for certification.</p>
          <div className="rounded-xl bg-[var(--wechr-subtle)] px-4 py-3 text-sm text-navy-800">Certification fee: <strong>{formatMoney(application.price_usd)} USD</strong> · payable after you submit</div>
          <Button className="btn-green" onClick={submit} disabled={submitting} data-testid="submit-application-button">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Submit for admin review
          </Button>
          <p className="flex items-center gap-1 text-xs text-[var(--wechr-muted-text)]"><Clock className="h-3 w-3" /> After submitting, you'll complete payment to enter the review queue.</p>
        </CardContent></Card>
      )}
      <div className="mt-6 flex justify-between"><Button variant="outline" onClick={goBack}>Back</Button></div>
    </div>
  );
};

const ApplicationWizard = () => {
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  const reload = async () => {
    const { data } = await appApi.get(id);
    setApplication(data);
  };

  useEffect(() => {
    appApi.get(id).then(({ data }) => {
      setApplication(data);
      // jump to a sensible starting step
      const submitted = ["submitted", "paid", "in_review", "needs_info", "enhanced_review", "approved", "rejected", "certified"].includes(data.status);
      if (submitted) setStep(3);
      else if ((data.documents || []).length) setStep(2);
      else if (Object.keys(data.questionnaire || {}).length) setStep(2);
    }).catch(() => toast.error("Application not found")).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const goNext = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  if (loading || !application) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-wgreen-600" /></div>;

  return (
    <div className="wechr-section">
      <div className="wechr-container">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-navy-900">{application.company_name}</h1>
            <p className="text-sm text-[var(--wechr-muted-text)]">Application · <span className="font-mono">{application.id.slice(0, 8)}</span></p>
          </div>
          <AppStatusBadge status={application.status} />
        </div>
        <div className="mb-8"><Stepper current={step} setStep={setStep} /></div>
        <Card><CardContent className="p-6 sm:p-8">
          {step === 0 && <StepProfile application={application} reload={reload} goNext={goNext} />}
          {step === 1 && <StepQuestionnaire application={application} reload={reload} goNext={goNext} goBack={goBack} />}
          {step === 2 && <StepDocuments application={application} reload={reload} goNext={goNext} goBack={goBack} />}
          {step === 3 && <SubmitStep application={application} reload={reload} goBack={goBack} />}
        </CardContent></Card>
      </div>
    </div>
  );
};

export default ApplicationWizard;
